import { Grid, Paper } from "@mui/material";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import { useEAFOptionFunctions } from "@/pages/eaf/eaf-processes/hooks/useEAFOptionFunctions";
import { AgGridReact } from "ag-grid-react";
import { ForwardedRef, forwardRef, useRef, useState } from "react";
import type { EAFProcessByShift } from "../../models/ProcessByShift";
import type { SelectedProcess } from "../models/SelectedProcess";
import { CommentModal } from "@/controls/ag-grid/components/CommentModal";
import { useEAFTableData } from "./useEAFTableData";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { getTableHeight, getMinTableHeight } from "@/utils/agGridUtils";
import { GRID_DIMENSIONS } from "@/utils/constants";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { upsertEAFProcessData } from "../../repositories/ProductionRepository";

interface Props {
  data: EAFProcessByShift[];
  onGridReady?: (params: GridReadyEvent) => void;
  selectedProcess: SelectedProcess | null;
  setSelectedProcess: (process: SelectedProcess | null) => void;
}

export const EAFProductionTable = forwardRef<AgGridReact, Props>(
  (
    {
      data,
      onGridReady: externalGridReady,
      selectedProcess,
      setSelectedProcess,
    },
    ref: ForwardedRef<AgGridReact>,
  ) => {
    const gridAPI = useRef<GridApi | null>(null);
    const [positionToOpenModal, setPositionToOpenModal] = useState<{
      top: number;
      left: number;
    }>();

    const handleShowCommentModal = (
      show: boolean,
      data: EAFProcessByShift,
      position?: { top: number; left: number },
    ) => {
      if (data) {
        setSelectedProcess(data);
      }
      setPositionToOpenModal(position);
      setShowCommentModal(show);
    };

    const { showCommentModal, setShowCommentModal } = useEAFOptionFunctions();
    const { columns } = useEAFTableData({
      showCommentModal,
      setShowCommentModal: handleShowCommentModal,
    });
    const { columnTypes } = useGridDefinitions({});

    const handleGridReady = (params: GridReadyEvent) => {
      gridAPI.current = params.api;
      params.api.sizeColumnsToFit();

      if (externalGridReady) {
        externalGridReady(params);
      }
    };

    return (
      <Grid container>
        <Grid
          component={Paper}
          elevation={3}
          item
          md={12}
          xs={12}
          sx={{
            height: getTableHeight(
              data.length,
              window.innerHeight,
              GRID_DIMENSIONS.ROW_HEIGHT * 2,
            ),
            minHeight: getMinTableHeight(GRID_DIMENSIONS.ROW_HEIGHT * 2),
            p: "10px",
            m: "20px 0 10px",
          }}
        >
          <AgGridReact<EAFProcessByShift>
            ref={ref}
            defaultColDef={defaultColDef}
            columnTypes={columnTypes}
            onGridReady={handleGridReady}
            rowData={data}
            columnDefs={columns}
            {...defaultGridOptions}
          />
          <CommentModal
            show={showCommentModal}
            onHide={() => {
              setShowCommentModal(false);
            }}
            position={positionToOpenModal}
            selectedElement={
              selectedProcess
                ? { ...selectedProcess, ElementId: selectedProcess.ProcessId }
                : null
            }
            setSelectedElement={setSelectedProcess}
            upsertFunction={upsertEAFProcessData}
          />
        </Grid>
      </Grid>
    );
  },
);
